import {
  collection,
  addDoc,
  doc,
  getDocs,
  query,
  where,
  runTransaction,
  orderBy,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  getMembershipVoucherPointsCost,
  MEMBERSHIP_VOUCHER_FACE_VALUE,
} from "../../config/membershipVoucherConfig";
import { CURRENT_ACTIVATION_FEE } from "../../config/membershipConfig";
import { FLAT_MEMBERSHIP_REFERRAL_PROFIT_MARGIN } from "../../config/membershipReferralConfig";
import CartPurchaseHandler from "../CartPurchaseHandler";
import AutoFillProgramService from "../autoFill/AutoFillProgramService";
import MultiLevelCommissionService from "../MultiLevelCommissionService";
import PackagePurchaseHandler from "../PackagePurchaseHandler";

const CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export function normalizeVoucherCode(raw) {
  return String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
}

function randomVoucherCode() {
  let suffix = "";
  for (let i = 0; i < 8; i++) {
    suffix += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  }
  return `UM-${suffix}`;
}

async function findUserDocRefByUid(uid) {
  const userQuery = query(collection(db, "users"), where("uid", "==", uid));
  const snap = await getDocs(userQuery);
  if (snap.empty) return null;
  return doc(db, "users", snap.docs[0].id);
}

export async function generateMembershipVoucher({
  generatorUid,
  generatorEmail,
  generatorName,
  pointsBalance,
}) {
  const { faceValue, adminFee, totalPoints } = getMembershipVoucherPointsCost();

  if (pointsBalance < totalPoints) {
    return {
      success: false,
      error: `Need ${totalPoints.toFixed(2)} pts (₹${faceValue} + ₹${adminFee.toFixed(2)} admin fee). You have ${pointsBalance.toFixed(2)} pts.`,
    };
  }

  const generatorRef = await findUserDocRefByUid(generatorUid);
  if (!generatorRef) {
    return { success: false, error: "User profile not found." };
  }

  let code = null;
  let lastError = null;

  for (let attempt = 0; attempt < 6; attempt++) {
    code = randomVoucherCode();
    const voucherRef = doc(db, "membershipVouchers", code);

    try {
      await runTransaction(db, async (tx) => {
        const userSnap = await tx.get(generatorRef);
        if (!userSnap.exists()) {
          throw new Error("User profile not found.");
        }
        const userData = userSnap.data();
        const balance = Number(userData.commissionBalance) || 0;
        if (balance < totalPoints) {
          throw new Error(
            `Insufficient Points Wallet balance (need ${totalPoints.toFixed(2)} pts).`
          );
        }

        const existing = await tx.get(voucherRef);
        if (existing.exists()) {
          throw new Error("CODE_COLLISION");
        }

        tx.set(voucherRef, {
          code,
          faceValue,
          pointsAmount: faceValue,
          adminFee,
          totalPointsDeducted: totalPoints,
          status: "active",
          generatedByUid: generatorUid,
          generatedByEmail: generatorEmail || userData.email,
          generatedByName: generatorName || userData.name,
          generatedAt: new Date().toISOString(),
          redeemedByUid: null,
          redeemedByEmail: null,
          redeemedByName: null,
          redeemedAt: null,
          redemptionOrderId: null,
        });

        tx.update(generatorRef, {
          commissionBalance: Math.max(0, balance - totalPoints),
          commissionUsed: (Number(userData.commissionUsed) || 0) + totalPoints,
          updatedAt: new Date().toISOString(),
        });
      });

      return { success: true, code, faceValue, adminFee, totalPoints };
    } catch (e) {
      if (e.message === "CODE_COLLISION") {
        lastError = e;
        continue;
      }
      return { success: false, error: e.message || "Failed to generate voucher." };
    }
  }

  return {
    success: false,
    error: lastError?.message || "Could not generate a unique code. Try again.",
  };
}

export async function redeemMembershipVoucher({
  code,
  redeemerUid,
  redeemerEmail,
  redeemerName,
}) {
  const normalized = normalizeVoucherCode(code);
  if (!normalized) {
    return { success: false, error: "Enter a voucher code." };
  }

  const redeemerRef = await findUserDocRefByUid(redeemerUid);
  if (!redeemerRef) {
    return { success: false, error: "User profile not found." };
  }

  const voucherRef = doc(db, "membershipVouchers", normalized);

  try {
    const redemptionMeta = await runTransaction(db, async (tx) => {
      const voucherSnap = await tx.get(voucherRef);
      if (!voucherSnap.exists()) {
        throw new Error("Invalid voucher code.");
      }
      const voucher = voucherSnap.data();
      if (voucher.status !== "active") {
        throw new Error("This voucher has already been used.");
      }
      if (voucher.generatedByUid === redeemerUid) {
        throw new Error("You cannot redeem a voucher you created.");
      }

      const userSnap = await tx.get(redeemerRef);
      if (!userSnap.exists()) {
        throw new Error("User profile not found.");
      }
      const userData = userSnap.data();
      if (userData.hasPurchasedPackage) {
        throw new Error("You already have Ultra membership.");
      }

      const now = new Date().toISOString();
      const orderId = `VOUCHER-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`;

      const purchasedPackageData = {
        id: "membership-voucher",
        name: "Ultra Membership (Voucher)",
        price: 0,
        description: `Activated with voucher ${normalized}`,
        category: "Membership Voucher",
        activationFee: voucher.faceValue || CURRENT_ACTIVATION_FEE,
        purchasedAt: now,
        orderId,
        confirmed: true,
        confirmedAt: now,
        voucherCode: normalized,
      };

      tx.update(redeemerRef, {
        hasPurchasedPackage: true,
        purchasedPackage: purchasedPackageData,
        purchasedActivationFee:
          voucher.faceValue || MEMBERSHIP_VOUCHER_FACE_VALUE,
        packagePurchaseDate: now,
        lastPackagePurchase: now,
        updatedAt: now,
      });

      tx.update(voucherRef, {
        status: "redeemed",
        redeemedByUid: redeemerUid,
        redeemedByEmail: redeemerEmail || userData.email,
        redeemedByName: redeemerName || userData.name,
        redeemedAt: now,
        redemptionOrderId: orderId,
      });

      return {
        orderId,
        voucher,
        buyerName: redeemerName || userData.name,
        buyerEmail: redeemerEmail || userData.email,
      };
    });

    try {
      await CartPurchaseHandler.addFirstTimeCashback(
        redeemerUid,
        CURRENT_ACTIVATION_FEE
      );
    } catch (cashbackErr) {
      console.error("Voucher redeem: cashback grant failed", cashbackErr);
    }

    try {
      const afResult =
        await AutoFillProgramService.enrollIncludedAutoFillForNewMembership({
          userId: redeemerUid,
          userEmail: redemptionMeta.buyerEmail,
          userName: redemptionMeta.buyerName,
          orderId: redemptionMeta.orderId,
          activationFee:
            redemptionMeta.voucher.faceValue || MEMBERSHIP_VOUCHER_FACE_VALUE,
        });
      if (afResult.created) {
        console.log(
          `Voucher redeem: Auto Fill program created ${afResult.programId}`
        );
      }
    } catch (afErr) {
      console.error("Voucher redeem: Auto Fill enrollment failed", afErr);
    }

    const activationFee =
      redemptionMeta.voucher.faceValue || MEMBERSHIP_VOUCHER_FACE_VALUE;

    await addDoc(collection(db, "cartOrders"), {
      cartOrderId: redemptionMeta.orderId,
      orderType: "package_purchase",
      orderStatus: "confirmed",
      paymentStatus: "voucher_redeemed",
      paymentMethod: "membership_voucher",
      isPackagePurchase: true,
      isSubItemPackage: true,
      buyerId: redeemerUid,
      buyerName: redemptionMeta.buyerName,
      buyerEmail: redemptionMeta.buyerEmail,
      packages: [
        {
          id: "membership-voucher",
          name: "Ultra Membership (Voucher)",
          price: 0,
          quantity: 1,
          category: "Membership Voucher",
          referralShare: true,
          profitMargin: FLAT_MEMBERSHIP_REFERRAL_PROFIT_MARGIN,
          isSubItemPackage: true,
          subItemsData: { activationFee },
        },
      ],
      subItemsData: { activationFee },
      packageTotal: 0,
      grandTotal: 0,
      totalAmount: 0,
      membershipVoucherCode: normalized,
      voucherGeneratedByUid: redemptionMeta.voucher.generatedByUid,
      voucherGeneratedByEmail: redemptionMeta.voucher.generatedByEmail,
      confirmedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    try {
      await MultiLevelCommissionService.processOrderCommissions({
        cartOrderId: redemptionMeta.orderId,
        buyerId: redeemerUid,
        buyerName: redemptionMeta.buyerName,
        buyerEmail: redemptionMeta.buyerEmail,
        packages: [
          {
            id: "membership-voucher",
            name: "Ultra Membership (Voucher)",
            price: 0,
            quantity: 1,
            category: "Membership Voucher",
            referralShare: true,
            profitMargin: FLAT_MEMBERSHIP_REFERRAL_PROFIT_MARGIN,
            isSubItemPackage: true,
            subItemsData: { activationFee },
          },
        ],
        items: [],
        totalAmount: 0,
        appliedDiscounts: {},
        referralSharingBlocked: false,
      });
    } catch (commissionErr) {
      console.error(
        "Voucher redeem: membership referral commissions failed",
        commissionErr
      );
    }

    try {
      await PackagePurchaseHandler.checkReferrerMilestones(redeemerUid);
    } catch (milestoneErr) {
      console.error("Voucher redeem: milestone check failed", milestoneErr);
    }

    return {
      success: true,
      message: "Ultra membership activated with your voucher!",
      orderId: redemptionMeta.orderId,
      code: normalized,
    };
  } catch (e) {
    return { success: false, error: e.message || "Redemption failed." };
  }
}

export async function listMembershipVouchersForAdmin() {
  const q = query(
    collection(db, "membershipVouchers"),
    orderBy("generatedAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listMembershipVouchersByGenerator(uid) {
  const q = query(
    collection(db, "membershipVouchers"),
    where("generatedByUid", "==", uid)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort(
      (a, b) =>
        new Date(b.generatedAt || 0).getTime() -
        new Date(a.generatedAt || 0).getTime()
    );
}

export async function getMembershipVoucherStats() {
  const all = await listMembershipVouchersForAdmin();
  return {
    total: all.length,
    active: all.filter((v) => v.status === "active").length,
    redeemed: all.filter((v) => v.status === "redeemed").length,
  };
}

export async function getVoucherByCode(code) {
  const normalized = normalizeVoucherCode(code);
  if (!normalized) return null;
  const snap = await getDoc(doc(db, "membershipVouchers", normalized));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
